import { Country } from '@axo/config/@types/Country.types';
import clsx from 'clsx';
import { useCallback } from 'react';
import TrustPilotLogoSVG from '../../@assets/brand/trustpilot-logo.svg?react';
import { i18n } from './i18n';
import { StarSize } from './TrustPilot.types';
import { TrustPilotStars } from './TrustPilotStars';
import styles from './TrustPilotWidget.module.scss';
import { Market, TrustPilotDataProps } from './TrustPilotWidget.types';

export const variants = [
  'combo-inline',
  'combo',
  'responsive',
  'mini-form',
  'micro-form',
] as const;

export type TrustPilotProps = {
  data: TrustPilotDataProps;
  market: Market;
  variant?: (typeof variants)[number];
  showNumberOfReviews?: boolean;
  className?: string;
};

export const TrustPilotWidget = ({
  data,
  market,
  variant = 'combo-inline',
  showNumberOfReviews = true,
  className,
}: TrustPilotProps) => {
  const TrustPilotLogo = useCallback(
    () => (
      <span className={styles.trustPilotLogo}>
        <TrustPilotLogoSVG />
      </span>
    ),
    []
  );

  const Stars = useCallback(
    ({ size }: { size?: StarSize }) => (
      <span className={styles.stars}>
        <TrustPilotStars rating={data.businessEntity.trustScore} size={size} />
      </span>
    ),
    [data.businessEntity.trustScore]
  );

  const TrustScore = useCallback(
    () => {
      if (data.businessEntity.trustScore < 4.5) {
        return null;
      }

      return (
        <span className={styles.trustScore}>
          TrustScore <b>{data.businessEntity.trustScore}</b>
        </span>
      );
    },
    [data.businessEntity.trustScore]
  );

  const NumberOfReviews = useCallback(
    () => (
      <span
        className={styles.numberOfReviews}
        dangerouslySetInnerHTML={{
          __html: `<b>${data.businessEntity.numberOfReviews.total}</b>&nbsp;${
            i18n[market.toLowerCase() as Lowercase<Country>]['reviews']
          }`,
        }}
      />
    ),
    [data.businessEntity.numberOfReviews.total, market]
  );

  const RatingText = useCallback(
    () => <span className={styles.ratingText}>{data.starsString}</span>,
    [data.starsString]
  );

  const BasedOn = useCallback(
    () =>
      showNumberOfReviews ? (
        <span className={styles.basedOn}>
          <NumberOfReviews />
        </span>
      ) : null,
    [showNumberOfReviews, NumberOfReviews]
  );

  const TrustScoreAndReviews = useCallback(
    () => (
      <div className={styles.trustScoreAndReviews}>
        <TrustScore /> {showNumberOfReviews && <NumberOfReviews />}
      </div>
    ),
    [NumberOfReviews, TrustScore, showNumberOfReviews]
  );

  const Widget = useCallback(
    ({ variant }: { variant: (typeof variants)[number] }) => (
      <span className={styles.widget}>
        {(variant === 'combo' ||
          variant === 'combo-inline' ||
          variant === 'responsive') && (
          <>
            <TrustPilotLogo />
            <Stars size={'lg'} />
            <TrustScoreAndReviews />
          </>
        )}
        {(variant === 'mini-form' || variant === 'micro-form') && (
          <>
            <RatingText />
            <Stars size={variant === 'micro-form' ? 'xs' : 'sm'} />
            <BasedOn />
            <TrustPilotLogo />
          </>
        )}
      </span>
    ),
    [TrustPilotLogo, Stars, TrustScoreAndReviews, RatingText, BasedOn, variant]
  );

  return (
    <div
      className={clsx(styles.trustPilotWidget, className)}
      data-variant={variant}
    >
      <Widget variant={variant} />
    </div>
  );
};
